/**
 * Tree Frog (Devio UCX Android) bundle matching helpers.
 *
 * Sits alongside the existing `bundleMatching.js` rather than modifying it. The
 * spec explicitly says: "Existing bundle matching logic applied to Tree Frog
 * designs without modification to the core algorithm." So this module mirrors the
 * shape of `matchBundle` (returns `{ bundle, excessMics, excessSpeakers }`) but
 * uses a separate selection path keyed on UCX hub size + room-bundle-first
 * fallback to compute bundle.
 *
 * Selection rules per the Tree Frog UC Platform & Equipment Selection Logic spec §5.5:
 *   - Bar-only design                      → UCX 1000
 *   - Bar + expansion mics or speakers     → UCX 2000
 *   - Non-bar primary mic (ceiling/table)  → UCX 2000, or UCX 3000 when a
 *     MRB-UCX3000T-VIDI280-* room bundle matches
 * (UCX 2000 and UCX 3000 are equally capable for audio; UCX 3000 only adds a
 * second HDMI output. Until Designer can tell which one a design needs — E.5 —
 * it recommends UCX 2000 unless a UCX 3000 room bundle matches.)
 *
 * Tree Frog room bundles (`MRB-UCX*`) are tried first; when none matches, the
 * compute bundle (`UCC-UCX*-T`) is used and all peripherals fall through to the
 * existing à la carte path.
 */
import { find } from 'lodash';
import equipmentCatalog from '../globalConstants/EquipmentCatalog';
import constants from '../toolConstants/conferenceroomDesigner/constants';
import { MIC_CATEGORIES } from '../types/models/microphone';
import {
  requiresTesiraUsbFallback,
  UCX_BUNDLE_SPEAKER_MODEL,
  CEILING_CHAIN_MAX_MICS,
  TABLE_CHAIN_MAX_MICS,
  UCX_SPEAKER_CAPS,
} from '../toolConstants/conferenceroomDesigner/treeFrogPorts';

/**
 * UCX hub identifiers used in bundle modelNames and `includes.ucCompute` values.
 */
export const UCX_HUB_SIZES = {
  UCX1000: 'Devio UCX 1000',
  UCX2000: 'Devio UCX 2000',
  UCX3000: 'Devio UCX 3000',
};

/**
 * Map from product-config model keys (state.microphones.model) to the bundle's
 * Tree Frog mic family identifier. Tree Frog bundles `includes.micModel` use
 * the abbreviated family code (`DCM-X` / `DTM-X`), not the full catalog model
 * name. State carries the legacy product key (`tcm_x` / `ttm_x`) — translate
 * once here so the rest of the matching stays bundle-shape-oriented.
 */
const TREE_FROG_MIC_FAMILY = {
  tcm_x: 'DCM-X',
  ttm_x: 'DTM-X',
};

/**
 * Returns true when the area's Platform selection makes TreeFrog bundle
 * matching applicable. Tied solely to `platform === 'Android'`.
 *
 * WDT-90 field-order fix: UC Compute (MTR/Zoom/None) is an additive choice
 * resolved AFTER Platform and Product Family, not a prerequisite for either
 * (see Tree Frog UC Platform & Equipment Selection Logic §2.2 — "the same
 * UCX device serves both platforms"). Requiring `ucCompute !== 'None'` here
 * used to make Android designs silently skip the Devio UCX bundle/hub (and,
 * for the port-overflow path, the UCC-UCX1000-T compute bridge + Tesira DSP)
 * whenever the user left UC Compute at its default. Gating on platform alone
 * lets an Android design resolve its Tree Frog equipment regardless of the
 * UC Compute value; UC Compute only ever adds a Lenovo-MTR/Zoom SKU on top
 * (see the `ucCompute` block in logic.js), never changes the Platform/Family
 * equipment itself. Tesira / Parlé / Devio SCX designs (Platform = Windows or
 * null) still fall through to the existing bundle logic untouched.
 *
 * @param {Object|null|undefined} area
 * @returns {boolean}
 */
export const isTreeFrogAndroidUC = (area) => {
  if (!area) return false;
  return area.platform === 'Android';
};

/**
 * True when the design uses the Devio UCX (Tree Frog) microphones — the DCM-X /
 * DTM-X twins — as opposed to the Parlé TCM-X / TTM-X names. This is the single
 * source of truth for "does this room speak Devio UCX?", consumed by both the
 * microphone display-name logic (Microphones menu / locations tab / placed devices)
 * and the equipment-list mic resolver, so every surface agrees.
 *
 * The distinguishing factor is **Devio family on the Android platform**. Devio SCX
 * is a Devio-family DSP on Windows (platform !== 'Android') and uses the Parlé names;
 * the WDT-90 port-overflow fallback keeps platform 'Android' but flips productFamily
 * to 'Tesira', so it too resolves to the Parlé names. UC Compute selection does not
 * change the mic branding, so this intentionally does NOT gate on ucCompute (unlike
 * isTreeFrogAndroidUC, which is about bundle/compute matching).
 *
 * @param {Object|null|undefined} area
 * @returns {boolean}
 */
export const isDevioUcxDesign = (area) =>
  Boolean(area) && area.productFamily === 'Devio' && area.platform === 'Android';

/**
 * UC Compute options that are not offered on a Devio UCX (Tree Frog) design.
 * Zoom Rooms has no Devio UCX compute SKU, so the option is removed from the UC
 * Compute list whenever the design resolves to Devio UCX (Devio family +
 * Android) and restored as soon as it doesn't — see the UC Compute selector's
 * `filter` in toolConstants/conferenceroomDesigner/menus.js.
 */
export const UCX_UNSUPPORTED_UC_COMPUTE = 'Zoom Rooms';

/**
 * The value a Devio UCX design falls back to when 'Zoom Rooms' was already
 * selected before the design became Devio UCX.
 *
 * 'Microsoft Teams' rather than 'None' on purpose: for a single bar mic design,
 * Family is derived from UC Compute (resolveBarProductFamily —
 * utilities/parleBarBundle.js): Android + Teams/Zoom → 'Devio', Android + None
 * → 'Parlé'. Falling back to 'None' would flip the family back off Devio UCX,
 * which re-adds Zoom Rooms to the list, which makes the design Devio UCX
 * again — an oscillation. 'Microsoft Teams' keeps the family stable AND keeps
 * the user's "this room has UC compute" intent.
 */
export const UCX_UC_COMPUTE_FALLBACK = 'Microsoft Teams';

/**
 * Normalizes an area's UC Compute value for the Devio UCX rule above.
 * Mutates the (immer draft) area in place and returns true when it changed
 * something, so the caller can surface a toast explaining the switch.
 *
 * Call this AFTER any reducer mutation that can settle `productFamily` /
 * `platform` / `ucCompute` — the UI filter alone can't cover a design that
 * BECOMES Devio UCX while 'Zoom Rooms' is already stored (the selector would
 * display a filtered value while state kept the Zoom SKU in the equipment list).
 *
 * @param {Object|null|undefined} area
 * @returns {boolean} true when `ucCompute` was changed
 */
export const normalizeUcComputeForUcx = (area) => {
  if (!area) return false;
  if (isDevioUcxDesign(area) && area.ucCompute === UCX_UNSUPPORTED_UC_COMPUTE) {
    area.ucCompute = UCX_UC_COMPUTE_FALLBACK;
    return true;
  }
  return false;
};

/**
 * Picks the UCX hub size from the system configuration (spec §5.5).
 *
 *   - Port overflow (incl. too many speakers for the speaker model) → UCX 1000 (Tesira via USB)
 *   - Non-bar primary mic (ceiling/table) → UCX 2000 (default model; a matching
 *     MRB-UCX3000T-VIDI280-* room bundle upgrades the design to UCX 3000 — that
 *     exception is applied in `findRoomBundle`, not here)
 *   - Bar primary + any expansion (mics or speakers) → UCX 2000
 *   - Bar primary only → UCX 1000
 *
 * UCX 2000 and UCX 3000 have the same ports and audio capacity (UCX 3000 adds a
 * second HDMI output), so this choice never changes port accounting. The UCX 3000
 * bar room bundles exist in the catalog but stay unmatched until Designer can
 * differentiate the two models (screen count or explicit user selection — E.5).
 *
 * @param {Object} area
 * @returns {'Devio UCX 1000' | 'Devio UCX 2000' | 'Devio UCX 3000'}
 */
export const getUcxHubSize = (area) => {
  // Tesira-via-USB fallback always uses UCX 1000 (USB to Tesira DSP) — a
  // port-budget overflow for the selected speaker model (or a speaker Devio
  // UCX can't drive at all). Wall / pendant / C-IC6LP / C-IC6LP-TAA speakers
  // within the port limits stay on Devio UCX (spec §5.3); they just never
  // match a room bundle (see `findRoomBundle`).
  if (requiresTesiraUsbFallback(area)) {
    return UCX_HUB_SIZES.UCX1000;
  }
  const micModel = area?.microphones?.model;
  const isBar = Boolean(micModel) && MIC_CATEGORIES.BAR.has(micModel);
  if (!isBar) {
    return UCX_HUB_SIZES.UCX2000;
  }
  const hasExpansionMics = area?.expansionMicType === 'ceiling' || area?.expansionMicType === 'table';
  const hasExpansionSpkrs = area?.expansionSpeakerType === 'ceiling' || area?.expansionSpeakerType === 'pendant';
  if (hasExpansionMics || hasExpansionSpkrs) {
    return UCX_HUB_SIZES.UCX2000;
  }
  return UCX_HUB_SIZES.UCX1000;
};

/**
 * Bar identifier as it appears in the bundle modelNames / `includes.bar` field
 * (`VBC 2500a` / `VBC 2800`). Returns null when the primary mic isn't a
 * matchable bar — non-bar primary mics route through the UCX 3000 bundles
 * keyed on camera + mic + speaker instead.
 *
 * @param {Object} area
 * @returns {string|null}
 */
const getBundleBar = (area) => {
  const modelName = area?.microphones?.modelName;
  if (!modelName) return null;
  if (modelName.includes('VBC 2800')) return 'VBC 2800';
  if (modelName.includes('VBC 2500a')) return 'VBC 2500a';
  // CBC / ABC bars aren't part of any defined UCX bundle today.
  return null;
};

/**
 * The mic family code (DCM-X / DTM-X) the area's mic configuration maps to.
 * Considers the primary mic first, then expansion mics — whichever drives the
 * Tree Frog chain. Returns null when neither maps to a Tree Frog family.
 *
 * @param {Object} area
 * @returns {string|null}
 */
const getBundleMicFamily = (area) => {
  // Bar designs have the bar as primary; the Tree Frog mic comes from expansion.
  const primaryModel = area?.microphones?.model;
  const isBar = primaryModel && MIC_CATEGORIES.BAR.has(primaryModel);
  if (isBar) {
    if (area?.expansionMicType === 'ceiling') return 'DCM-X';
    if (area?.expansionMicType === 'table')   return 'DTM-X';
    return null;
  }
  return TREE_FROG_MIC_FAMILY[primaryModel] ?? null;
};

/**
 * Mic quantity for matching — sum primary (non-bar) + expansion. Bar designs
 * contribute 0 from the primary (the bar isn't a mic chain entry).
 *
 * @param {Object} area
 * @returns {number}
 */
const getBundleMicQty = (area) => {
  const primaryModel = area?.microphones?.model;
  const isBar = primaryModel && MIC_CATEGORIES.BAR.has(primaryModel);
  const primaryQty = isBar ? 0 : (area?.microphones?.qty ?? area?.microphones?.layout?.length ?? 0);
  const expansionQty = area?.expansionMicrophones?.qty ?? area?.expansionMicrophones?.layout?.length ?? 0;
  return primaryQty + expansionQty;
};

/**
 * Speaker quantity for matching — sum primary + expansion.
 *
 * @param {Object} area
 * @returns {number}
 */
const getBundleSpkrQty = (area) => {
  const primaryQty = area?.speakers?.qty ?? area?.speakers?.layout?.length ?? 0;
  const expansionQty = area?.expansionSpeakers?.qty ?? area?.expansionSpeakers?.layout?.length ?? 0;
  return primaryQty + expansionQty;
};

/**
 * Capacity of the devices a Tree Frog room bundle already contains — Tree Frog
 * UC Platform & Equipment Selection Logic §6.2 "Expanded contents":
 *   - Ceiling bundles: the first mic is a DCM-XA (integrated amp, 4 C-IC6), the
 *     second (if any) a DCM-XEX. One chain → 1 Ethernet port.
 *   - Table bundles with speakers: DTM-X (+ DTM-XEX) and a DPA-450BP (8 C-IC6).
 *   - Bar bundles (no speakers): a plain mic chain, no amplification.
 *
 * `mics` / `speakers` are the TOTAL counts those devices can carry. A partial
 * match (§5.3a) may add extras only up to these totals — anything beyond needs
 * an additional Ethernet port device (another mic head unit or a DPA-450BP),
 * which drops the bundle.
 *
 * Room bundles only ever ship the standard C-IC6 (2 per amp channel), so the
 * pendant/wall capacity variants don't apply here.
 *
 * @param {Object} bundle
 * @returns {{ mics: number, speakers: number }}
 */
export const getRoomBundleCapacity = (bundle) => {
  const includes = bundle?.includes ?? {};
  if (!includes.micModel) return { mics: 0, speakers: 0 };
  const isTable = includes.micModel === 'DTM-X';
  const chainMax = isTable ? TABLE_CHAIN_MAX_MICS : CEILING_CHAIN_MAX_MICS;
  const chains = Math.ceil((includes.micQty ?? 0) / chainMax);
  let speakers = 0;
  if ((includes.spkrQty ?? 0) > 0) {
    speakers = isTable ? UCX_SPEAKER_CAPS.ceiling.dpa : chains * UCX_SPEAKER_CAPS.ceiling.headAmp;
  }
  return { mics: chains * chainMax, speakers };
};

/**
 * True when the design's extras on top of a room bundle fit inside the devices
 * the bundle already contains (§5.3a "Partial match"). Extras that would need a
 * new Ethernet port device mean "No match".
 */
const extrasFitBundle = (bundle, excessMics, excessSpeakers) => {
  const capacity = getRoomBundleCapacity(bundle);
  const spareMics = Math.max(0, capacity.mics - (bundle.includes?.micQty ?? 0));
  const spareSpeakers = Math.max(0, capacity.speakers - (bundle.includes?.spkrQty ?? 0));
  return excessMics <= spareMics && excessSpeakers <= spareSpeakers;
};

/** Sentinel for "the user turned the camera off" — never equals a bundle's camera. */
const CAMERA_OFF = 'none';

/**
 * The standalone camera in the design, as a catalog name (e.g. 'Vidi 280'), for
 * the room-bundle camera check (§5.3a criterion 3):
 *   - camera explicitly turned off (`hasCamera === false`) → CAMERA_OFF, which
 *     never matches a bundle camera — a design without a camera must not ship
 *     the bundle's Vidi 280;
 *   - no camera data at all (legacy / partial area) → null, treated as
 *     compatible (Android auto-adds a Vidi 280 — §5.6 — the same camera every
 *     UCX 3000 room bundle ships);
 *   - otherwise the model's display name from the tool constants, so a camera
 *     added or renamed in constants.js is picked up here too.
 *
 * @param {Object} area
 * @returns {string|null}
 */
const getDesignCameraName = (area) => {
  const cameras = area?.cameras;
  if (!cameras) return null;
  if (cameras.hasCamera === false) return CAMERA_OFF;
  const model = cameras.model && cameras.model !== 'none' ? cameras.model : null;
  return (model && constants.equipment?.cameras?.[model]?.modelName) || cameras.modelName || model;
};

/**
 * Find the room bundle that matches the configuration. Bar designs are keyed on
 * the bar model + mic family + expansion qty; non-bar designs are keyed on the
 * standalone camera + mic family + speaker count against the UCX 3000 room
 * bundles (MRB-UCX3000T-VIDI280-*).
 *
 * Returns the bundle entry plus the excess quantities (positive when the user
 * has more peripherals than the bundle covers, zero otherwise). Returns null
 * when no room bundle is a viable subset of the design.
 *
 * @param {Object} area
 * @param {string} ucxSize
 * @returns {{ bundle: Object, excessMics: number, excessSpeakers: number } | null}
 */
const findRoomBundle = (area, ucxSize) => {
  const bar = getBundleBar(area);
  const micFamily = getBundleMicFamily(area);
  const totalMicQty = getBundleMicQty(area);
  const totalSpkrQty = getBundleSpkrQty(area);

  // Bar designs route through MRB-UCX{1000,2000}T-* bundles. Per the spec the
  // UCX 3000 bar bundles are deliberately not selected today — bar designs with
  // expansion ride the UCX 2000 family even when UCX 3000 would also fit.
  if (bar) {
    if (ucxSize !== UCX_HUB_SIZES.UCX1000 && ucxSize !== UCX_HUB_SIZES.UCX2000) return null;

    // Candidate filter: same hub, same bar, same mic family (if applicable).
    // Matched on partNumber (the stable, unprefixed SKU) rather than modelName
    // — modelName is the customer-facing display string ("Biamp MRB-...",
    // PIG-546) and shouldn't drive matching logic.
    const candidates = equipmentCatalog.Bundles.filter(b => {
      if (!b.partNumber?.startsWith('MRB-UCX')) return false;
      if (b.includes?.ucCompute !== ucxSize) return false;
      if (b.includes?.bar !== bar) return false;
      if (b.includes?.camera) return false; // bar bundles don't include a standalone camera
      if (micFamily) return b.includes?.micModel === micFamily;
      return !b.includes?.micModel; // no expansion mics → bundle shouldn't define micModel either
    });

    if (candidates.length === 0) return null;
    const bundle = candidates[0]; // Bar bundles are 1:1 — no ambiguity once filtered.
    const bundleMicQty = bundle.includes?.micQty ?? 0;
    const excessMics = Math.max(0, totalMicQty - bundleMicQty);
    // Only expansion speakers count here — the bar's own integrated speaker
    // rides the bar over USB and is part of the bar itself.
    const expansionSpkrQty = area?.expansionSpeakers?.qty ?? area?.expansionSpeakers?.layout?.length ?? 0;
    const excessSpeakers = Math.max(0, expansionSpkrQty - (bundle.includes?.spkrQty ?? 0));
    // §5.3a: extras must fit the bundle's own devices. A bar bundle has no
    // amplification, so any expansion speaker needs a DPA-450BP (new port
    // device) and a second mic chain needs a new head unit — both drop the
    // bundle in favour of the compute bundle + à la carte.
    if (!extrasFitBundle(bundle, excessMics, excessSpeakers)) return null;
    return { bundle, excessMics, excessSpeakers };
  }

  // Non-bar designs try the MRB-UCX3000T-VIDI280-* room bundles. The resolved
  // default model is UCX 2000 (§5.5), but a UCX 3000 room bundle that matches on
  // every other §5.3a criterion sets the model to UCX 3000 (§5.3a criterion 1 /
  // §5.5 exception). Among the bundles the design fully contains and whose
  // devices can carry the extras, pick the one that covers the most; the extras
  // are added à la carte. A Tesira-via-USB fallback (UCX 1000) never matches.
  if (ucxSize === UCX_HUB_SIZES.UCX1000) return null;

  const spkrModelKey = area?.speakers?.model;
  const designCamera = getDesignCameraName(area);

  // Matched on partNumber, not modelName — see the bar-branch comment above.
  const candidates = equipmentCatalog.Bundles.filter(b => {
    if (!b.partNumber?.startsWith('MRB-UCX3000T-VIDI280')) return false;
    if (b.includes?.ucCompute !== UCX_HUB_SIZES.UCX3000) return false;
    if (!micFamily) return false; // need a Tree Frog mic for non-bar bundles
    if (b.includes?.micModel !== micFamily) return false;
    // The bundle's speakers must be the design's speakers. Every UCX 3000 room
    // bundle ships the standard C-IC6 (`includes.spkrModel: 'C-IC6'`) — a
    // design with wall / pendant / C-IC6LP / C-IC6LP-TAA speakers must never
    // match one (that's how a wall-speaker room used to show "2CIC6" bundles).
    // A missing `speakers.model` (legacy data) is not used to reject.
    if (b.includes?.spkrModel && spkrModelKey && spkrModelKey !== UCX_BUNDLE_SPEAKER_MODEL) return false;
    // §5.3a criterion 3: the camera must match (Vidi 280 for these bundles).
    if (b.includes?.camera && designCamera && designCamera !== b.includes.camera) return false;
    // The bundle's `includes.micQty` and `spkrQty` must not exceed the design.
    if ((b.includes?.micQty ?? 0) > totalMicQty) return false;
    if ((b.includes?.spkrQty ?? 0) > totalSpkrQty) return false;
    // §5.3a partial match: extras only up to the capacity of the bundle's own
    // devices. E.g. 2 ceiling mics + 6 C-IC6 needs a DPA-450BP (new port
    // device), so no bundle applies; 2DCMX-4CIC6 allows no extras at all.
    const excessMics = totalMicQty - (b.includes?.micQty ?? 0);
    const excessSpeakers = totalSpkrQty - (b.includes?.spkrQty ?? 0);
    if (!extrasFitBundle(b, excessMics, excessSpeakers)) return false;
    return true;
  });

  if (candidates.length === 0) return null;

  // Prefer the candidate that covers the most (highest micQty × 10 + spkrQty —
  // mics weigh more so a 2-DCM-X bundle wins over a 4-CIC6 one with 1 DCM-X).
  candidates.sort((a, b) =>
    ((b.includes?.micQty ?? 0) * 10 + (b.includes?.spkrQty ?? 0)) -
    ((a.includes?.micQty ?? 0) * 10 + (a.includes?.spkrQty ?? 0))
  );
  const bundle = candidates[0];
  return {
    bundle,
    excessMics: Math.max(0, totalMicQty - (bundle.includes?.micQty ?? 0)),
    excessSpeakers: Math.max(0, totalSpkrQty - (bundle.includes?.spkrQty ?? 0)),
  };
};

/**
 * Main entry point. Returns null when the area doesn't qualify for Tree Frog
 * bundle matching (non-Devio family, no UC compute, etc.) — caller falls
 * through to the existing `matchBundle` path unchanged.
 *
 * When applicable, returns `{ bundle, type, excessMics, excessSpeakers }` where
 * `type` is 'room' (room bundle matched, peripherals partially covered) or
 * 'compute' (only the compute bundle matched, all peripherals are excess and
 * route through the à la carte path).
 *
 * @param {Object} area
 * @returns {{
 *   bundle: Object,
 *   type: 'room' | 'compute',
 *   excessMics: number,
 *   excessSpeakers: number,
 * } | null}
 */
export const matchTreeFrogBundle = (area) => {
  if (!isTreeFrogAndroidUC(area)) return null;

  const ucxSize = getUcxHubSize(area);

  // 1. Try the room bundle first.
  const roomMatch = findRoomBundle(area, ucxSize);
  if (roomMatch) {
    return { ...roomMatch, type: 'room' };
  }

  // 2. Fall back to the compute bundle. All peripherals become excess and the
  //    existing à la carte path adds individual catalog entries on top.
  const ucxSuffix = ucxSize.replace('Devio UCX ', ''); // 'Devio UCX 1000' → '1000'
  const computeBundlePartNumber = `UCC-UCX${ucxSuffix}-T`;
  // Matched on partNumber, not modelName — see the bar-branch comment above.
  const computeBundle = find(equipmentCatalog.Bundles, b => b.partNumber === computeBundlePartNumber);
  if (!computeBundle) return null;

  return {
    bundle: computeBundle,
    type: 'compute',
    excessMics: getBundleMicQty(area),
    excessSpeakers: getBundleSpkrQty(area),
  };
};
